---
title: "Как перевести время между часовыми поясами и не ошибиться из-за летнего времени"
description: "Практический разбор перевода времени между городами: пояса IANA, переходы на летнее время, неоднозначные часы и проверка даты встречи."
locale: "ru"
date: "2026-07-23"
tags: ["часовые пояса", "время", "DST", "IANA"]
relatedTools: ["time-zones"]
translations:
  en: "how-to-convert-time-between-time-zones"
faq:
  - q: "Почему нельзя просто прибавить постоянную разницу в часах?"
    a: "Потому что правила летнего времени зависят от страны и даты. Разница между двумя городами может меняться в течение года."
  - q: "Можно ли использовать сокращения CST, IST или BST?"
    a: "Лучше выбрать город или часовой пояс IANA. Такие сокращения неоднозначны и могут означать разные регионы, поэтому dateify их отклоняет."
  - q: "Что происходит со временем в ночь перевода часов?"
    a: "Весной некоторые локальные минуты не существуют, а осенью один и тот же час встречается дважды. dateify показывает ошибку для пропуска и предлагает выбрать ранний или поздний вариант для повтора."
  - q: "Хранит ли Unix timestamp часовой пояс?"
    a: "Нет. Timestamp задаёт момент времени, но не сохраняет исходный город или часовой пояс. Для последующего отображения зону нужно знать отдельно."
---

В приглашении написано: «созвон 1 ноября 2026 года в 01:30 по Нью-Йорку».
Простое `+8 часов` здесь ненадёжно: в эту дату часы переводят назад, и 01:30
наступает дважды — сначала при смещении `UTC−04:00`, затем при
`UTC−05:00`. Для обычной даты достаточно выбрать города, но рядом с переходом
на летнее или зимнее время нужно зафиксировать ещё и конкретное вхождение часа.

Откройте [конвертер часовых поясов](/chasovye-poyasa), задайте исходные дату,
время и город, затем добавьте нужные зоны. Инструмент сравнивает один и тот же
момент сразу в 2–5 поясах и показывает локальные дату, время и смещение UTC.

## Короткий порядок проверки

1. Уточните, в каком городе или часовом поясе IANA задано исходное время.
2. Введите именно локальные дату и время из приглашения.
3. Добавьте города участников, а не постоянные поправки вроде «плюс три часа».
4. Проверьте дату результата: в другом поясе уже может быть предыдущий или
   следующий день.
5. Если конвертер сообщает о переходе часов, не игнорируйте предупреждение.
6. Отправляйте итог вместе с названиями зон, например:
   `2026-07-23 09:00 Europe/Moscow (UTC+03:00) /
   2026-07-23 08:00 Europe/Paris (UTC+02:00)`.

Такой формат заметно надёжнее фразы «в девять по Москве»: он оставляет
проверяемый контекст и не заставляет получателя угадывать дату или сезонное
смещение.

## Город надёжнее сокращения

`UTC+3` — фиксированное смещение. `Europe/Moscow` — зона с историей правил.
Для текущей даты результат может совпасть, но это не одинаковые данные.
Часовой пояс IANA, привязанный к городу, позволяет применить правила,
действовавшие в конкретную дату, включая переходы на летнее время там, где они
используются.

Сокращения ещё опаснее. `CST`, `IST`, `AST` и `BST` встречаются в разных
регионах и не задают однозначную зону. Поэтому dateify не пытается угадать их
по контексту. Выберите город или полный идентификатор вроде
`America/New_York`, `Asia/Kolkata` или `Europe/London`.

Не все смещения кратны целому часу. Например, встречаются зоны с `+05:30` и
`+05:45`. Ручная арифметика «по часам» легко теряет эти 30 или 45 минут, а
таблица с местным временем и смещением относительно UTC показывает их явно.

## Что происходит при переводе часов

Весенний переход создаёт пропуск. Если часы переводят с 01:59 сразу на 03:00,
локального времени 02:30 в эту дату не существует. Dateify не переносит такой
ввод молча на следующий допустимый час, а показывает ошибку. Для встречи это
повод выбрать другое время и заново отправить приглашение.

Осенний переход создаёт повтор: один и тот же местный час бывает дважды с
разными смещениями относительно UTC. В этом случае конвертер показывает оба допустимых
вхождения и позволяет явно выбрать раннее или позднее. Без такого выбора
фраза «01:30» не определяет один точный момент.

| Ситуация | Что зафиксировать |
| --- | --- |
| Обычный день | Дату, время и часовой пояс IANA |
| Весенний пропуск | Выбрать реально существующее время |
| Осенний повтор | Раннее или позднее вхождение |
| Несколько офисов | Результат для каждого города и смену даты |
| Повторяющаяся встреча | Проверить даты до и после сезонного перехода |

## Почему timestamp не решает исходную неоднозначность

Unix timestamp уже обозначает один момент, поэтому его удобно переводить в
разные локальные представления. Но timestamp не хранит название исходной зоны.
Если у вас есть только `01:30` и город с осенним повтором, сначала нужно
выбрать одно из двух вхождений и только потом получится единственный timestamp.

Если задача начинается с числа из API или лога, сначала проверьте
[секунды и миллисекунды](/blog/unix-timestamp-sekundy-ili-millisekundy). Если
же исходные данные — дата, время и город, используйте конвертер зон: это другой
тип входа и другая проверка.

## Перед отправкой приглашения

- Укажите город или часовой пояс IANA, а не неоднозначное сокращение.
- Проверьте, не сменилась ли календарная дата у другого участника.
- Для осеннего повтора сохраните выбранное вхождение.
- Для серии встреч проверьте несколько дат: страны переходят на сезонное время
  не синхронно.
- Если встреча критична, продублируйте точный момент в календарном приглашении.

Конвертер работает локально в браузере. Ссылка для обмена хранит дату, время,
зоны и выбор повторного часа в части после `#`; эта часть остаётся в браузере
и не отправляется серверу. При этом правила часовых поясов могут меняться
законами, а браузер использует установленную у него базу зон. Для далёкой
будущей даты после объявленного изменения правил результат стоит перепроверить
ближе к событию.
